<template>
  <div id="app" :class="{ 'app-authenticated': authStore.isLogged }">
    <BModalOrchestrator />
    <BToastOrchestrator />
    <Alert />
    <Header />
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { provide, onMounted } from 'vue'
import { BModalOrchestrator, BToastOrchestrator } from 'bootstrap-vue-next'
import Header from '@/components/Header.vue'
import Alert from '@/components/Global/Alert.vue'
import { saveAs } from 'file-saver'
import * as chat from '@/utils/chat'
import { useRootStore } from '@/stores/useRootStore'
import { useAuthStore } from '@/stores/useAuthStore'

const rootStore = useRootStore()
const authStore = useAuthStore()

function sendFile(response: any) {
  const fileName = response.headers['content-disposition'].split('filename=')[1].replace(/['"]/g, '')
  const type = response.headers['content-type']
  const blob = new Blob([response.data], { type, responseType: 'arraybuffer' } as any)
  saveAs(blob, fileName)
}

provide('sendFile', sendFile)

async function initChat() {
  const user = rootStore.user
  window.addEventListener('chatwoot:ready', function () {
    if (user) {
      chat.addUserData(user)
    }
  })
  return new Promise<void>((resolve) => {
    const script = document.createElement('script')
    script.onload = () => {
      resolve()
      ;(window as any).chatwootSDK.run({
        websiteToken: 'F3Rji2qD1us4sT9YserRYBoz',
        baseUrl: 'https://chat.infodiagram.com'
      })
    }
    script.async = true
    script.defer = true
    script.src = 'https://chat.infodiagram.com/packs/js/sdk.js'
    document.head.appendChild(script)
  })
}

onMounted(async () => {
  await initChat()
})
</script>

<style lang="scss">
  /* Tokens FIRST so :root custom properties are available everywhere
     downstream (including legacy --c-* aliases that the rest of the
     SCSS still references). */
  @import './assets/scss/tokens';
  @import './assets/scss/var';
  @import 'bootstrap/scss/bootstrap';
  @import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
  @import './assets/scss/fonts';
  @import './assets/scss/base';
  @import './assets/scss/pages';
  @import './assets/scss/tour';

  #app.app-authenticated {
    --app-shell-sidebar-width: 276px;
    --app-shell-topbar-height: 88px;
    background:
      radial-gradient(circle at top left, rgba(247, 153, 57, 0.1), transparent 26%),
      linear-gradient(180deg, #fffaf5 0%, #fff 18%, #f6f1f8 100%);
    min-height: 100vh;
  }

  #app.app-authenticated main,
  #app.app-authenticated .page {
    margin-left: calc(var(--app-shell-sidebar-width) + 24px);
    padding-top: calc(var(--app-shell-topbar-height) + 28px);
    padding-right: 24px;
  }

  #app.app-authenticated .page {
    margin-top: 0;
    margin-bottom: 0;
    padding-bottom: 40px;
  }

  @media screen and (max-width: 991px) {
    #app.app-authenticated {
      --app-shell-sidebar-width: 0px;
      --app-shell-topbar-height: 76px;
    }

    #app.app-authenticated main,
    #app.app-authenticated .page {
      margin-left: 0;
      padding-top: calc(var(--app-shell-topbar-height) + 18px);
      padding-right: 16px;
      padding-left: 16px;
    }
  }
</style>
